import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { addItem, removeItem } from '../actions/items';
import { PROTECTION_PLAN_ROUTE } from '../constants';
import { fetchDRAdminConfigClient } from '../utils/fetchDRAdminConfigClient';
import { getMmdId } from '../utils/getMmdId';

const useProtectionPlansFallback = () => {
	const dispatch = useDispatch();
	const {
		items,
		labels,
		prefs,
		requestData,
		siteConfigProperties
	} = useSelector((state) => state);

	const stateValues = useMemo(() => ({
		items,
		labels,
		prefs,
		requestData,
		siteConfigProperties
	}), [items, labels, prefs, requestData, siteConfigProperties]);

	useEffect(() => {
		const protectionPlanItem = stateValues.items.find(
			(item) => item.name === 'protectionPlans'
		);
		// Only run fallback if protection plans are missing OR in loading state
		if (protectionPlanItem && !protectionPlanItem.isLoading) {
			return;
		}

		const checkRequirementsAndAdd = async () => {
			// Resolve mmdId using shared utility (Async because PrivateAPI can be a Promise)
			const mmdIdResult = await getMmdId(null, stateValues.requestData);
			const mmdId = mmdIdResult?.code;
			const pageAlias = stateValues.requestData?.pageAlias;

			const isVDPDREnabled =
				stateValues.siteConfigProperties?.enableVDPDigitalRetailing?.trim() === 'all' ||
				stateValues.siteConfigProperties?.enableVDPDigitalRetailing
					?.replaceAll(' ', '')
					.split(',')
					.includes(pageAlias);

			const showAmdProtectionPlans = stateValues.prefs?.showAmdProtectionPlans !== 'false';
			const enableVehicleProtection = stateValues.prefs?.enableProtection === 'true';

			if (mmdId && isVDPDREnabled && showAmdProtectionPlans) {
				try {
					const drConfig = await fetchDRAdminConfigClient(mmdId);

					if (drConfig?.error) {
						if (protectionPlanItem?.isLoading) {
							dispatch(removeItem('protectionPlans'));
						}
						return;
					}

					const hasFi = drConfig?.products?.hasFi || drConfig?.hasFi;

					if (hasFi) {
						dispatch(
							addItem({
								name: 'protectionPlans',
								iconName: 'shield-check',
								title: stateValues.labels.PROTECTION_PLAN_HEADING,
								content: [
									'GAP Protection',
									'Lifetime Powertrain',
									'Tires and Wheels',
									'Paint Protection'
								],
								linkUrl: '',
								linkText: stateValues.labels.REVIEW_PROTECTION_PLANS,
								opensModal: enableVehicleProtection,
								modalContent: null,
								openSdpRoute: PROTECTION_PLAN_ROUTE,
								isLoading: false
							})
						);
					} else if (protectionPlanItem?.isLoading) {
						// Placeholder present, but dealer has no F&I
						dispatch(removeItem('protectionPlans'));
					}
				} catch (e) {
					if (protectionPlanItem?.isLoading) {
						dispatch(removeItem('protectionPlans'));
					}
					// eslint-disable-next-line no-console
					console.error(
						'ws-vehicle-confidence: Failed to fetch fallback drConfig',
						e
					);
				}
			} else if (protectionPlanItem?.isLoading) {
				dispatch(removeItem('protectionPlans'));
			}
		};

		checkRequirementsAndAdd();

	}, [stateValues, dispatch]);
};

export default useProtectionPlansFallback;
